import { useMemo } from 'react';
import * as d3Force from 'd3-force';
import { getChromosomeLayout, getYScale } from '../layout';
import type { Peak } from '../types';

// Label dimensions for collision detection
const LABEL_HEIGHT = 12;
const CHAR_WIDTH = 6;
const LABEL_PADDING = 6;
const MIN_LABEL_SPACING = 10;
const LABEL_ANGLE = -45; // Degrees
const TOP_PADDING = 15; // Minimum space above topmost label
const BOTTOM_PADDING = 8; // Space between labels and plot

/** Gene with evidence (burden or coding) in a peak */
export interface ImplicatedGene {
  geneId: string;
  geneSymbol: string;
  hasBurden: boolean;
  hasCoding: boolean;
  burdenTypes: string[];
  lofCount: number;
  missenseCount: number;
  bestCodingHgvsp?: string;
}

export interface PeakLabelNode {
  peak: Peak;
  targetX: number;
  targetY: number; // Y position on the plot (normalized, 0-1)
  isNearestOnly: boolean;
  x: number;
  y: number;
  /** Gene symbol of the top gene */
  label: string;
  /** Gene ID (ENSG...) of the top gene */
  geneId: string;
  labelWidth: number;
  hasBurden: boolean;
  codingCount: number;
  collisionRadius: number;
  /** Array of burden annotation types found (e.g., ['pLoF', 'missenseLC']) */
  burdenTypes: string[];
  /** Whether the top gene has significant coding variants */
  hasCoding: boolean;
  /** Count of LoF variants in the top gene */
  lofCount: number;
  /** Count of missense variants in the top gene */
  missenseCount: number;
  /** Total number of implicated genes in this peak */
  implicatedCount: number;
  /** Whether this is a burden-only peak (no GWAS single-variant signal) */
  isBurdenOnly: boolean;
  /** All genes with burden or coding evidence in this peak */
  implicatedGenes: ImplicatedGene[];
  /** Whether this peak should have its label text rendered (vs just a hoverable dot) */
  isLabeled: boolean;
}

export interface PeakLabelLayout {
  nodes: PeakLabelNode[];
  labelAreaHeight: number;
}

/**
 * Estimates the width of a label string in pixels
 */
function estimateLabelWidth(
  label: string,
  burdenTypes: string[],
  hasCoding: boolean,
  bestCodingHgvsp: string | undefined,
  implicatedCount: number,
  isBurdenOnly: boolean = false,
  isNearestOnly: boolean = false
): number {
  // Line 1: burden indicators + gene name + implicated count
  let line1 = label;
  if (isNearestOnly) line1 = 'nearest: ' + line1;
  if (isBurdenOnly) line1 = '◆ ' + line1;
  if (burdenTypes.length > 0) line1 = '●'.repeat(burdenTypes.length) + ' ' + line1;
  if (implicatedCount > 1) line1 += ` +${implicatedCount - 1}`;

  const hasBurden = burdenTypes.length > 0;

  // If both burden and coding: two-line label, width = max of lines
  if (hasCoding && hasBurden && bestCodingHgvsp) {
    const line2 = `${bestCodingHgvsp}↑`;
    const maxLen = Math.max(line1.length, line2.length);
    return maxLen * CHAR_WIDTH + LABEL_PADDING * 2;
  }

  // Single line: coding appended inline
  if (hasCoding) {
    if (bestCodingHgvsp) line1 += ` (${bestCodingHgvsp}↑)`;
    else line1 += ' (C)';
  }
  return line1.length * CHAR_WIDTH + LABEL_PADDING * 2;
}

// Minimal HGVSp parser to condense p.Val600Glu to V600E
function parseHgvspLabel(hgvsp: string | undefined | null): string {
  if (!hgvsp) return '';
  const match = hgvsp.match(/p\.([A-Z][a-z]{2})(\d+)([A-Z][a-z]{2})/);
  if (match) {
    const aa3to1: Record<string, string> = {
      Ala: 'A', Arg: 'R', Asn: 'N', Asp: 'D', Cys: 'C',
      Glu: 'E', Gln: 'Q', Gly: 'G', His: 'H', Ile: 'I',
      Leu: 'L', Lys: 'K', Met: 'M', Phe: 'F', Pro: 'P',
      Ser: 'S', Thr: 'T', Trp: 'W', Tyr: 'Y', Val: 'V', Ter: '*',
    };
    return `p.${aa3to1[match[1]] || match[1]}${match[2]}${aa3to1[match[3]] || match[3]}`;
  }
  const shortMatch = hgvsp.match(/p\.([A-Z])(\d+)([A-Z*])/);
  if (shortMatch) return `p.${shortMatch[1]}${shortMatch[2]}${shortMatch[3]}`;
  const synMatch = hgvsp.match(/p\.([A-Z][a-z]{2})(\d+)=/);
  if (synMatch) {
    const aa3to1: Record<string, string> = {
      Ala: 'A', Arg: 'R', Asn: 'N', Asp: 'D', Cys: 'C',
      Glu: 'E', Gln: 'Q', Gly: 'G', His: 'H', Ile: 'I',
      Leu: 'L', Lys: 'K', Met: 'M', Phe: 'F', Pro: 'P',
      Ser: 'S', Thr: 'T', Trp: 'W', Tyr: 'Y', Val: 'V',
    };
    return `p.${aa3to1[synMatch[1]] || synMatch[1]}${synMatch[2]}=`;
  }
  return hgvsp;
}

const SIG_THRESHOLD = 2.5e-6;

interface PeakGene {
  gene_id: string;
  gene_symbol: string;
  burden_results?: Array<{
    annotation: string;
    pvalue?: number;
    pvalue_burden?: number;
    pvalue_skat?: number;
  }>;
  coding_variant_count?: number;
  lof_count?: number;
  missense_count?: number;
  distance_kb?: number;
  best_coding_csq?: string;
  best_coding_hgvsp?: string;
  best_coding_hgvsc?: string;
  best_coding_ac?: number;
}

/**
 * Selects the best representative gene for a given peak based on:
 * 1. Has significant burden or coding variants
 * 2. Best burden p-value
 * 3. Highest coding variant count
 * 4. Physical distance to peak
 */
function getBestGeneForPeak(peak: Peak): PeakGene | undefined {
  if (!peak.genes || peak.genes.length === 0) return undefined;

  return [...peak.genes].sort((a, b) => {
    // Helper to get minimum p-value from burden results
    const getMinP = (br: { pvalue?: number; pvalue_burden?: number; pvalue_skat?: number }) =>
      Math.min(br.pvalue ?? Infinity, br.pvalue_burden ?? Infinity, br.pvalue_skat ?? Infinity);

    const aMinP = Math.min(...(a.burden_results?.map(getMinP) || [Infinity]));
    const bMinP = Math.min(...(b.burden_results?.map(getMinP) || [Infinity]));

    const aBurdenSig = aMinP < SIG_THRESHOLD;
    const bBurdenSig = bMinP < SIG_THRESHOLD;

    const aCodingCount = a.coding_variant_count || 0;
    const bCodingCount = b.coding_variant_count || 0;

    const aImplicated = aBurdenSig || aCodingCount > 0;
    const bImplicated = bBurdenSig || bCodingCount > 0;

    // 1. Prefer implicated genes
    if (aImplicated && !bImplicated) return -1;
    if (!aImplicated && bImplicated) return 1;

    // 2. Best burden p-value
    if (aMinP !== bMinP) return aMinP - bMinP;

    // 3. Highest coding variant count
    if (aCodingCount !== bCodingCount) return bCodingCount - aCodingCount;

    // 4. Distance (closest physical proximity as tie-breaker)
    return (a.distance_kb || 0) - (b.distance_kb || 0);
  })[0];
}

/**
 * Hook that computes peak label positions and the required label area height.
 * Returns positioned nodes and the computed height needed for labels.
 * @param peaks Peak data from the API
 * @param width Container width in pixels
 * @param height Container height in pixels
 * @param contig Layout contig - 'all' for genome-wide, or specific chromosome
 */
export interface ExclusionZone {
  /** X coordinate of left edge */
  x: number;
  /** Y coordinate of top edge */
  y: number;
  /** Width of the zone */
  width: number;
  /** Height of the zone */
  height: number;
}

export function usePeakLabelLayout(
  peaks: Peak[] | undefined,
  width: number,
  height: number,
  contig: string = 'all',
  maxLabels: number = 25,
  customLabelMode: boolean = false,
  selectedPeakIds: Set<string> = new Set(),
  exclusionZone?: ExclusionZone
): PeakLabelLayout {
  const layout = getChromosomeLayout(contig);
  const yScale = getYScale();

  return useMemo(() => {
    if (!peaks || peaks.length === 0 || width === 0 || height === 0) {
      return { nodes: [], labelAreaHeight: 0 };
    }

    // Filter peaks to only those on the selected chromosome (if not 'all')
    const filteredPeaks = contig === 'all'
      ? peaks
      : peaks.filter((p) => {
          const peakContig = p.contig.startsWith('chr') ? p.contig : `chr${p.contig}`;
          return peakContig === contig;
        });

    // Check if a peak has implicated genes (burden or coding evidence)
    const isImplicated = (p: Peak): boolean =>
      p.genes.some((g) => {
        const hasBurden = g.burden_results?.some((b) =>
          ((b.pvalue ?? Infinity) < SIG_THRESHOLD) ||
          ((b.pvalue_burden ?? Infinity) < SIG_THRESHOLD) ||
          ((b.pvalue_skat ?? Infinity) < SIG_THRESHOLD)
        );
        const hasCoding = (g.lof_count || 0) > 0 || (g.missense_count || 0) > 0 || (g.coding_variant_count || 0) > 0;
        return hasBurden || hasCoding;
      });

    // Determine which peaks should be labeled
    const labeledPeakIds = new Set<string>();

    if (customLabelMode) {
      selectedPeakIds.forEach(id => labeledPeakIds.add(id));
    } else {
      // Combine all peaks (GWAS + burden-only) and take top N overall,
      // prioritizing implicated peaks then by p-value
      const allCandidates = [...filteredPeaks]
        .sort((a, b) => {
          const aImpl = isImplicated(a);
          const bImpl = isImplicated(b);
          if (aImpl !== bImpl) return aImpl ? -1 : 1;
          return a.pvalue - b.pvalue;
        })
        .slice(0, maxLabels);
      allCandidates.forEach(p => labeledPeakIds.add(`${p.contig}-${p.position}`));
    }

    const nodes: PeakLabelNode[] = [];

    // Process ALL filtered peaks (not just top ones)
    for (const peak of filteredPeaks) {
      const xNorm = layout.getX(peak.contig, peak.position);
      const yNorm = yScale.getY(peak.pvalue, peak.neg_log10_p);

      if (xNorm === null) {
        continue;
      }

      // Defensive: ensure genes array exists
      if (!peak.genes || peak.genes.length === 0) {
        continue;
      }

      const topGene = getBestGeneForPeak(peak);
      if (!topGene) {
        continue;
      }

      // Collect burden types and implicated genes from all genes in this peak
      const burdenTypesSet = new Set<string>();
      const implicatedGenes: ImplicatedGene[] = [];
      for (const g of peak.genes) {
        // Collect gene-specific burden types
        const geneBurdenTypes: string[] = [];
        if (g.burden_results) {
          for (const b of g.burden_results) {
            if ((b.pvalue ?? Infinity) < SIG_THRESHOLD ||
                (b.pvalue_burden ?? Infinity) < SIG_THRESHOLD ||
                (b.pvalue_skat ?? Infinity) < SIG_THRESHOLD) {
              burdenTypesSet.add(b.annotation);
              geneBurdenTypes.push(b.annotation);
            }
          }
        }
        const hasGeneBurden = geneBurdenTypes.length > 0;
        const geneLofCount = g.lof_count || 0;
        const geneMissenseCount = g.missense_count || 0;
        const hasGeneCoding = geneLofCount > 0 || geneMissenseCount > 0 || (g.coding_variant_count || 0) > 0;

        if (hasGeneBurden || hasGeneCoding) {
          implicatedGenes.push({
            geneId: g.gene_id,
            geneSymbol: g.gene_symbol,
            hasBurden: hasGeneBurden,
            hasCoding: hasGeneCoding,
            burdenTypes: geneBurdenTypes,
            lofCount: geneLofCount,
            missenseCount: geneMissenseCount,
            bestCodingHgvsp: g.best_coding_hgvsp ? parseHgvspLabel(g.best_coding_hgvsp) : (g.best_coding_hgvsc ? g.best_coding_hgvsc.split(':').pop() : undefined),
          });
        }
      }
      const implicatedCount = implicatedGenes.length;
      const burdenTypes = Array.from(burdenTypesSet);
      const hasBurden = burdenTypes.length > 0;

      // Extract specific coding variant counts
      const lofCount = topGene.lof_count || 0;
      const missenseCount = topGene.missense_count || 0;
      const codingCount = topGene.coding_variant_count || 0;
      const hasCoding = codingCount > 0;

      // Pull best HGVS string
      const bestCodingHgvsp = topGene.best_coding_hgvsp ? parseHgvspLabel(topGene.best_coding_hgvsp) : (topGene.best_coding_hgvsc ? topGene.best_coding_hgvsc.split(':').pop() : undefined);

      const isBurdenOnly = peak.isBurdenOnly ?? false;
      const isNearestOnly = !hasBurden && !hasCoding;
      const labelWidth = estimateLabelWidth(topGene.gene_symbol, burdenTypes, hasCoding, bestCodingHgvsp, implicatedCount, isBurdenOnly, isNearestOnly);

      // For angled labels, calculate collision radius
      const angleRad = Math.abs(LABEL_ANGLE * Math.PI / 180);
      const effectiveWidth = labelWidth * Math.cos(angleRad) + LABEL_HEIGHT * Math.sin(angleRad);
      const effectiveHeight = labelWidth * Math.sin(angleRad) + LABEL_HEIGHT * Math.cos(angleRad);
      const collisionRadius = Math.max(effectiveWidth, effectiveHeight) / 2 + MIN_LABEL_SPACING;

      const targetX = xNorm * width;
      const targetY = yNorm; // Store normalized Y for now

      const peakId = `${peak.contig}-${peak.position}`;
      const isLabeled = labeledPeakIds.has(peakId);

      nodes.push({
        peak,
        targetX,
        targetY,
        isNearestOnly,
        x: targetX,
        y: 0, // Will be computed by force simulation
        label: topGene.gene_symbol,
        geneId: topGene.gene_id,
        labelWidth,
        hasBurden,
        codingCount,
        collisionRadius,
        burdenTypes,
        hasCoding,
        lofCount,
        missenseCount,
        implicatedCount,
        isBurdenOnly,
        implicatedGenes,
        isLabeled,
      });
    }

    if (nodes.length === 0) {
      return { nodes: [], labelAreaHeight: 0 };
    }

    // Sort by x position
    nodes.sort((a, b) => a.targetX - b.targetX);

    // Extract ONLY labeled nodes for D3 force simulation
    const labeledNodes = nodes.filter(n => n.isLabeled);

    if (labeledNodes.length === 0) {
      // No labeled nodes — no label area needed, but still return all nodes for ghost dots
      return { nodes, labelAreaHeight: 0 };
    }

    // Initial pass: position labels at a baseline closer to the data
    const baseline = 50;
    for (const node of labeledNodes) {
      node.y = baseline;
    }

    // Run force simulation on labeled nodes only
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const simulation = d3Force.forceSimulation(labeledNodes as any)
      .force('x', d3Force.forceX((d: PeakLabelNode) => d.targetX).strength(0.2))
      .force('y', d3Force.forceY(baseline).strength(0.1))
      .force('collide', d3Force.forceCollide((d: PeakLabelNode) => d.collisionRadius * 1.2).strength(1).iterations(6))
      .stop();

    for (let i = 0; i < 400; i++) {
      simulation.tick();
    }

    resolveOverlaps(labeledNodes);

    // Push labels out of the exclusion zone (e.g., QQ inset area)
    if (exclusionZone) {
      const ez = exclusionZone;
      for (const node of labeledNodes) {
        const angleRad = Math.abs(LABEL_ANGLE * Math.PI / 180);
        const labelRight = node.x + node.labelWidth * Math.cos(angleRad);
        const labelTop = node.y - node.labelWidth * Math.sin(angleRad);
        const labelBottom = node.y + LABEL_HEIGHT * Math.cos(angleRad);

        if (labelRight > ez.x && node.x < ez.x + ez.width &&
            labelBottom > ez.y && labelTop < ez.y + ez.height) {
          if (ez.x < width / 2) {
             node.x = ez.x + ez.width + MIN_LABEL_SPACING;
          } else {
             node.x = ez.x - node.labelWidth * Math.cos(angleRad) - MIN_LABEL_SPACING;
          }
        }
      }
      resolveOverlaps(labeledNodes);
    }

    // Find the vertical extent of labeled nodes only
    const angleRad = Math.abs(LABEL_ANGLE * Math.PI / 180);
    let minY = Infinity;
    let maxY = -Infinity;
    for (const node of labeledNodes) {
      const upwardExtent = node.labelWidth * Math.sin(angleRad);
      const downwardExtent = LABEL_HEIGHT * Math.cos(angleRad);
      minY = Math.min(minY, node.y - upwardExtent);
      maxY = Math.max(maxY, node.y + downwardExtent);
    }

    const labelExtent = maxY - minY;
    const labelAreaHeight = Math.ceil(labelExtent + TOP_PADDING + BOTTOM_PADDING);

    const shift = TOP_PADDING - minY;
    for (const node of labeledNodes) {
      node.y += shift;
      const rightExtent = node.labelWidth * Math.cos(angleRad);
      const leftMargin = 10;
      const rightMargin = Math.max(10, rightExtent);
      node.x = Math.max(leftMargin, Math.min(width - rightMargin, node.x));
    }

    return { nodes, labelAreaHeight };
  }, [peaks, width, height, layout, yScale, contig, maxLabels, customLabelMode, selectedPeakIds, exclusionZone]);
}

/**
 * Resolve remaining overlaps by pushing labels apart.
 * Prefers horizontal spreading over vertical stacking to keep labels closer to data.
 * Runs multiple passes to ensure all overlaps are resolved.
 */
function resolveOverlaps(nodes: PeakLabelNode[]): void {
  // Sort by X position for consistent processing
  const sorted = [...nodes].sort((a, b) => a.x - b.x);

  // Run multiple passes until no overlaps remain
  for (let pass = 0; pass < 15; pass++) {
    let hadOverlap = false;

    for (let i = 0; i < sorted.length; i++) {
      const current = sorted[i];

      for (let j = 0; j < sorted.length; j++) {
        if (i === j) continue;
        const other = sorted[j];

        if (labelsOverlap(current, other)) {
          hadOverlap = true;

          // Calculate overlap amounts in each direction
          const dx = current.x - other.x;
          const dy = current.y - other.y;
          const minDist = (current.collisionRadius + other.collisionRadius) * 0.7;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist === 0) {
            // Same position - push apart horizontally
            current.x += minDist / 2;
          } else {
            // Prefer horizontal separation: use a 2:1 bias for horizontal vs vertical
            const pushAmount = (minDist - dist) / 2 + MIN_LABEL_SPACING;
            const horizontalBias = 2;

            if (Math.abs(dx) * horizontalBias > Math.abs(dy)) {
              // Push horizontally
              const direction = dx >= 0 ? 1 : -1;
              current.x += direction * pushAmount;
            } else {
              // Push vertically (only if horizontal won't work well)
              // Move the one with higher Y (lower on screen) up above the other
              if (current.y >= other.y) {
                current.y = other.y - LABEL_HEIGHT - MIN_LABEL_SPACING;
              } else {
                other.y = current.y - LABEL_HEIGHT - MIN_LABEL_SPACING;
              }
            }
          }
        }
      }
    }

    if (!hadOverlap) break;
  }
}

/**
 * Check if two labels overlap using their collision radii
 */
function labelsOverlap(a: PeakLabelNode, b: PeakLabelNode): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  // Use larger multiplier for more conservative collision detection
  const minDist = (a.collisionRadius + b.collisionRadius) * 0.7;
  return dist < minDist;
}
